import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaMapMarkerAlt } from "react-icons/fa";
import { fetchPotholes } from "../store/potholeSlice";
import { STATUSES } from "../globals/misc/statuses";

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { potholes, status } = useSelector((state) => state.pothole);

  useEffect(() => {
    dispatch(fetchPotholes());
  }, [dispatch]);

  const myPotholes = useMemo(() => {
    if (!user?.id) return [];
    return potholes.filter((p) => {
      const creatorId = p.createdBy?._id || p.createdBy?.id || p.createdBy;
      return String(creatorId) === String(user.id);
    });
  }, [potholes, user]);

  if (status === STATUSES.LOADING && potholes.length === 0) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-navy-800" />
      </div>
    );
  }

  return (
    <div className="mx-auto min-h-screen max-w-6xl px-4 py-10">
      <div className="mb-8">
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-amber-600">
          Dashboard
        </p>
        <h1 className="mt-2 font-display text-4xl font-bold text-navy-900">
          Your pothole reports
        </h1>
        <p className="mt-2 text-navy-800/70">
          Welcome{user?.name ? `, ${user.name}` : ""}. Track what you&apos;ve
          submitted.
        </p>
      </div>

      {myPotholes.length === 0 ? (
        <div className="rounded-2xl border border-navy-900/10 bg-white p-10 text-center">
          <FaMapMarkerAlt className="mx-auto mb-4 text-4xl text-navy-700/40" />
          <h2 className="font-display text-2xl font-semibold text-navy-900">
            No reports yet
          </h2>
          <p className="mt-2 text-navy-800/70">
            Submit your first hazard to see it here.
          </p>
          <button
            type="button"
            onClick={() => navigate("/report")}
            className="mt-6 rounded-xl bg-amber-500 px-6 py-2.5 font-semibold text-navy-950 hover:bg-amber-400"
          >
            Report a pothole
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {myPotholes.map((pothole) => (
            <button
              key={pothole._id}
              type="button"
              onClick={() => navigate(`/pothole/${pothole._id}`)}
              className="overflow-hidden rounded-2xl border border-navy-900/10 bg-white text-left shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
            >
              {pothole.images?.url ? (
                <img
                  src={pothole.images.url}
                  alt={pothole.address}
                  className="h-40 w-full object-cover"
                />
              ) : (
                <div className="flex h-40 items-center justify-center bg-surface text-navy-700/40">
                  <FaMapMarkerAlt className="text-3xl" />
                </div>
              )}
              <div className="p-4">
                <h3 className="font-display text-lg font-semibold text-navy-900 line-clamp-2">
                  {pothole.address}
                </h3>
                <p className="mt-1 text-sm capitalize text-navy-800/70">
                  {pothole.status} · {pothole.severity}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
